/**
 * TeamPage — ADMIN personnel management: list company users + add a new user.
 * Uses POST /api/auth/users (seat-quota gated server-side) — the created user
 * receives an invite token when no password is supplied.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, UserPlus, X, Upload } from 'lucide-react';
import { api, authApi, importApi } from '../utils/api';

// S1 LARGE-tier: the server's seat/crew quota 402s carry a JSON payload as the
// message — parse it into a human sentence instead of rendering raw JSON.
// LUNA-DELTA-001: shared util so every quota surface (Team, Accept Invite)
// renders the same friendly message; re-exported here for existing imports.
import { friendlyQuotaError } from '../utils/quotaError';
export { friendlyQuotaError };

// Numeric-aware crew-name sort so 'Crew 2' precedes 'Crew 10'.
export function sortCrews<T extends { name?: string | null }>(crews: T[]): T[] {
  return [...crews].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true }));
}
import { generateTempPassword as tempPassword } from '../utils/tempPassword';
import CertBadge from '../components/CertBadge';
import CsvImportPanel, { canUseCsvImport } from '../components/CsvImportPanel';

// BP3: downloadable team import template. Real emails create an invite at
// COMMIT time only — never on preview.
const TEAM_IMPORT_TEMPLATE = [
  'firstName,lastName,email,role,employeeId,phoneNumber',
  'Dana,Diaz,dana@yourcompany.com,CREW_MEMBER,E-104,555-0100',
  'Sam,Reyes,sam@yourcompany.com,FOREMAN,E-105,',
].join('\n');

const ROLE_OPTIONS = [
  { value: 'FOREMAN', label: 'Foreman' },
  { value: 'CREW_MEMBER', label: 'Worker' },
  // Fix 4 (UAT): 'WORKER' is an accepted deprecated alias — server normalizes to CREW_MEMBER.
  { value: 'WORKER', label: 'Worker (legacy)' },
  { value: 'SAFETY_MANAGER', label: 'Safety Manager' },
  { value: 'PROJECT_MANAGER', label: 'Project Manager' },
  { value: 'CONSTRUCTION_MANAGER', label: 'Construction Manager' },
  { value: 'CONSTRUCTION_SUPERVISOR', label: 'Construction Supervisor' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'READ_ONLY', label: 'Read Only' },
] as const;

const ROLE_STYLES: Record<string, string> = {
  ADMIN: 'bg-slate-100 text-slate-700',
  SAFETY_MANAGER: 'bg-teal-100 text-teal-700',
  PROJECT_MANAGER: 'bg-indigo-100 text-indigo-700',
  CONSTRUCTION_MANAGER: 'bg-orange-100 text-orange-700',
  CONSTRUCTION_SUPERVISOR: 'bg-amber-100 text-amber-700',
  FOREMAN: 'bg-blue-100 text-blue-700',
  CREW_MEMBER: 'bg-green-100 text-green-700',
  READ_ONLY: 'bg-gray-100 text-gray-600',
};

// UAT-003: honest, reason-specific copy for a refused/failed invite email.
// The server reports emailError; a "sent" claim is only ever shown for a
// real provider-accepted send.
export function inviteEmailFailureCopy(reason?: string | null): string {
  switch (reason) {
    case 'reserved_tld_address':
      return 'the address uses a reserved test domain (.test/.example/.invalid) that can never receive mail';
    case 'placeholder_test_address':
      return 'the address is an internal paper-worker placeholder that never receives mail';
    case 'email_not_configured':
      return 'no email provider is configured for this environment';
    case 'no_recipients':
      return 'the address was blank';
    case 'network_error':
      return 'the mail provider could not be reached';
    default:
      return reason ? `the mail provider rejected the send (${reason})` : 'the send did not complete';
  }
}

// UAT-005: one-line "used of limit" summary for the seat buckets.
export function seatUsageSummary(seats: { plan: string; buckets: { bucket: string; used: number; limit: number }[] } | null): string {
  if (!seats) return '';
  return seats.buckets
    .map((b) => (b.limit < 0 ? `${b.bucket}: ${b.used} (unlimited)` : `${b.bucket}: ${b.used}/${b.limit} used`))
    .join(' · ');
}

export default function TeamPage() {
  const [users, setUsers] = useState<any[]>([]);
  const [crews, setCrews] = useState<any[]>([]);
  const [crewName, setCrewName] = useState('');
  // S2 crew-naming (Brian 2026-09-29): crew name defaults to the foreman's
  // name ("<First Last>'s Crew"); the name field stays editable by admin/manager.
  const [crewForemanId, setCrewForemanId] = useState<number | ''>('');
  const [crewSaving, setCrewSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteEmailSent, setInviteEmailSent] = useState<boolean | null>(null);
  // UAT-003 (user-POV 2026-10-06): the server now distinguishes a real send
  // from a refused/failed one and reports WHY (emailError) — surface it.
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  // UAT-005 (user-POV 2026-10-06): seat-capacity context for the add-person
  // flow — current used/limit per role bucket, shown BEFORE the submit fails.
  const [seatUsage, setSeatUsage] = useState<{ plan: string; buckets: { bucket: string; used: number; limit: number }[] } | null>(null);
  // UAT-003: the address the invite email was addressed to (the form resets
  // right after a successful create, so capture it for the honest copy).
  const [invitedEmail, setInvitedEmail] = useState('');
  const [newCredentials, setNewCredentials] = useState<{ email: string; password: string } | null>(null);
  const [showBadges, setShowBadges] = useState(false);
  const [showImport, setShowImport] = useState(false); // BP3: CSV bulk import
  // BP3 audit L4: import UI is gated to ADMIN/SUPER_ADMIN (server routes are
  // requireRole fail-closed; this only hides the entry point).
  const canImport = canUseCsvImport();
  const [expandedBadgeUser, setExpandedBadgeUser] = useState<any>(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', role: 'CREW_MEMBER', phoneNumber: '', employeeId: '' });

  const load = useCallback(() => {
    setLoading(true);
    authApi.listCompanyUsers()
      .then((res: any) => {
        // Server wraps ALL JSON as {success, data}; authApi returns the
        // wrapper — unwrap res.data.data (Badge UAT fix: /badge/undefined QRs
        // came from rendering the wrapper object instead of the user array).
        const list = res?.data?.data ?? res?.data;
        setUsers(Array.isArray(list) ? list : []);
      })
      .catch((e: any) => setError(e?.response?.data?.message || 'Could not load your team.'))
      .finally(() => setLoading(false));
    // Crews (field crews = Team rows) — used by project crew assignment
    try { api.get('/projects/crews').then((res: any) => setCrews(sortCrews(res?.data?.data ?? []))).catch(() => {}); } catch { /* api mock may not expose crews in some tests */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // UAT-005: load seat usage whenever the Add person panel opens so the
  // admin sees used/limit per role bucket BEFORE submitting.
  useEffect(() => {
    if (!showAdd) return;
    authApi.getSeatUsage()
      .then((res: any) => setSeatUsage(res?.data ?? res))
      .catch(() => setSeatUsage(null));
  }, [showAdd]);

  // ── Fix 3 (UAT): admin user-management actions ─────────────────────────────

  // Luna round-1 LOW-4: temp passwords now come from a
  // crypto.getRandomValues-based generator (>= 16 chars, mixed classes).
  const generateTempPassword = tempPassword;

  const handleResetPassword = async (u: any) => {
    const temp = generateTempPassword();
    if (!confirm(`Generate a temporary password for ${u.firstName} ${u.lastName}? It will be shown once after this.`)) return;
    setError(''); setSuccess('');
    try {
      await authApi.adminResetPassword(u.id, temp);
      setSuccess(`Temporary password for ${u.firstName} ${u.lastName}: ${temp} — share it securely and have them change it after first login.`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Password reset failed.');
    }
  };

  const handleDeactivate = async (u: any) => {
    if (!confirm(`Deactivate ${u.firstName} ${u.lastName}? Their project assignments will be closed.`)) return;
    setError(''); setSuccess('');
    try {
      const res = await authApi.deactivateUser(u.id);
      setSuccess(res.message || 'User deactivated.');
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Deactivation failed.');
    }
  };

  const handleReactivate = async (u: any) => {
    setError(''); setSuccess('');
    try {
      const res = await authApi.reactivateUser(u.id);
      setSuccess(res.message || 'User reactivated.');
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Reactivation failed.');
    }
  };

  const handleDeleteUser = async (u: any) => {
    if (!confirm(`Permanently DELETE ${u.firstName} ${u.lastName}? This cannot be undone. Prefer Deactivate unless this is an abandoned signup.`)) return;
    setError(''); setSuccess('');
    try {
      await authApi.deleteUser(u.id);
      setSuccess('User deleted.');
      load();
    } catch (e: any) {
      // Show error payloads verbatim (blockers list).
      setError(e?.response?.data?.message || 'Delete failed.');
    }
  };

  const addUser = async () => {
    setSaving(true); setError(''); setSuccess(''); setInviteToken(''); setNewCredentials(null); setInvitedEmail(form.email);
    try {
      const payload: any = { firstName: form.firstName, lastName: form.lastName, role: form.role };
      if (form.email) payload.email = form.email;
      if (form.password) payload.password = form.password;
      if (form.phoneNumber) payload.phoneNumber = form.phoneNumber;
      if (form.employeeId) payload.employeeId = form.employeeId;
      const res: any = await authApi.createUser(payload);
      setSuccess(res.message || 'User created successfully');
      if (res.inviteToken) {
        setInviteToken(res.inviteToken);
        // S2 invite truth + UAT-003: tell the admin whether the email ACTUALLY
        // went out (provider-accepted), and WHY it did not when it didn't —
        // with the copyable accept-invite link as the fallback.
        setInviteEmailSent(res.emailSent === true);
        setInviteEmailError(res.emailError ?? null);
        setInviteUrl(res.inviteUrl ?? null);
      } else {
        setInviteEmailSent(null);
        setInviteEmailError(null);
        setInviteUrl(null);
      }
      if (res.generatedEmail && res.data?.email) {
        setNewCredentials({ email: res.data.email, password: res.generatedPassword ?? '' });
      }
      setForm({ firstName: '', lastName: '', email: '', password: '', role: 'CREW_MEMBER', phoneNumber: '', employeeId: '' });
      setShowAdd(false);
      load();
    } catch (e: any) {
      setError(friendlyQuotaError(e, 'Could not add this user.'));
    } finally {
      setSaving(false);
    }
  };

  const copyCredentials = async () => {
    if (!newCredentials) return;
    const text = `Email: ${newCredentials.email}\nPassword: ${newCredentials.password}`;
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copied email + password to clipboard.');
    } catch {
      setSuccess(text);
    }
  };

  // ── S1 LARGE-tier: crew (Team) creation — the only self-serve crews path ──
  // SOL-FINAL-006 (external audit 2026-10-06, MEDIUM): setCrewSaving is
  // async — two rapid clicks both passed the stale-name check and both
  // POSTed, burning two quota slots. This ref is a SYNCHRONOUS in-flight
  // guard: set before the first await, checked on every invocation.
  const crewSubmittingRef = useRef(false);
  const createCrew = async () => {
    if (crewSubmittingRef.current) return;
    if (!crewName.trim() && !crewForemanId) return;
    // UAT-004 (user-POV 2026-10-06): rapid repeat submission created
    // duplicate-named crews and burned the quota. Two guards: the button is
    // disabled while pending (crewSaving), and an EXISTING name now requires
    // an explicit confirmation instead of silently creating a twin.
    const foreman = crewForemanId ? users.find((u: any) => u.id === crewForemanId) : null;
    const nameToUse = crewName.trim() || (foreman ? `${foreman.firstName ?? ''} ${foreman.lastName ?? ''}'s Crew` : '');
    if (nameToUse && crews.some((c: any) => (c.name ?? '').trim().toLowerCase() === nameToUse.toLowerCase())) {
      const proceed = confirm(`A crew named “${nameToUse}” already exists — create ANOTHER crew with the same name? Cancel to pick a different name (nothing is created or counted).`);
      if (!proceed) {
        setSuccess('');
        setError(`No crew created — “${nameToUse}” already exists. How to fix: choose a different crew name, or confirm the dialog if a duplicate really is intended.`);
        return;
      }
    }
    crewSubmittingRef.current = true;
    setCrewSaving(true); setError(''); setSuccess('');
    try {
      const res: any = await api.post('/projects/crews', {
        name: crewName.trim() || undefined,
        foremanId: crewForemanId || undefined,
      });
      setSuccess(res.message || `Crew created${foreman ? ` as “${res?.data?.name ?? ''}”` : ''}.`);
      setCrewName('');
      setCrewForemanId('');
      const crewsRes = await api.get('/projects/crews');
      setCrews(sortCrews(crewsRes?.data?.data ?? []));
    } catch (e: any) {
      setError(friendlyQuotaError(e, 'Could not create the crew.'));
    } finally {
      crewSubmittingRef.current = false;
      setCrewSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-violet-100 flex items-center justify-center">
              <Users className="h-6 w-6 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Team</h1>
              <p className="text-sm text-gray-500" data-testid="team-count">
                {loading ? <span data-testid="team-count-loading">Loading team…</span> : <>{users.length} {users.length === 1 ? 'person' : 'people'} in your company</>}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canImport ? (
              <button
                onClick={() => { setShowImport(!showImport); setShowAdd(false); setError(''); setSuccess(''); }}
                data-testid="toggle-team-import"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
              >
                <Upload className="h-4 w-4" /> Import CSV
              </button>
            ) : (
              <span
                title="CSV import is available to admins only."
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed"
                data-testid="team-import-unavailable"
              >
                <Upload className="h-4 w-4" /> Import CSV (admins only)
              </span>
            )}
            <button
              onClick={() => { setShowAdd(!showAdd); setShowImport(false); setError(''); setSuccess(''); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
            >
              {showAdd ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
              {showAdd ? 'Close' : 'Add person'}
            </button>
          </div>
        </div>

        {canImport && showImport && (
          <CsvImportPanel
            title="Import your team from CSV"
            template={TEAM_IMPORT_TEMPLATE}
            columns={['firstName', 'lastName', 'email', 'role', 'employeeId', 'phoneNumber']}
            previewFn={importApi.teamPreview}
            commitFn={importApi.teamCommit}
            onDone={load}
          />
        )}

        {success && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
            {success}
            {inviteToken && (
              <div className="mt-2 text-xs">
                {inviteEmailSent ? (
                  <p className="font-medium">✓ Invite email accepted by the mail provider and queued for delivery to {invitedEmail || 'their address'}.</p>
                ) : inviteEmailSent === false ? (
                  <p className="font-medium text-amber-700">
                    ✗ Invite email could NOT be sent — why: {inviteEmailFailureCopy(inviteEmailError)}. How to fix: share the link below with them directly (valid 24 hours).
                  </p>
                ) : (
                  <p className="font-medium text-amber-700">Share this link or invite code with them so they can set a password (valid 24 hours):</p>
                )}
                {inviteUrl && (
                  <div className="mt-1 flex items-center gap-2">
                    <code className="px-1.5 py-0.5 bg-white border border-green-200 rounded font-mono break-all">{inviteUrl}</code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(inviteUrl).catch(() => {})}
                      className="px-2 py-0.5 rounded border border-green-300 text-green-700 hover:bg-green-50"
                    >
                      Copy link
                    </button>
                  </div>
                )}
                <div className="mt-1">
                  <span className="font-semibold">Invite code</span> (backup, paste on the Accept Invite page):
                  <code className="ml-1 px-1.5 py-0.5 bg-white border border-green-200 rounded font-mono break-all">{inviteToken}</code>
                </div>
              </div>
            )}
            {newCredentials && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                <p className="font-semibold">⚠ Copy these now — they won't be shown again.</p>
                <div className="mt-2 space-y-1 text-xs">
                  <div>
                    <span className="font-semibold">Email:</span>{' '}
                    <code className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono break-all">{newCredentials.email}</code>
                  </div>
                  {newCredentials.password && (
                    <div>
                      <span className="font-semibold">Password:</span>{' '}
                      <code className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono break-all">{newCredentials.password}</code>
                    </div>
                  )}
                </div>
                <button onClick={copyCredentials} className="mt-2 px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700">
                  Copy email + password
                </button>
              </div>
            )}
          </div>
        )}
        {error && !showAdd && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

        {/* Badges toggle — compact cert badge per user (blueprint §4) */}
        <div className="mb-4 flex items-center justify-between bg-white rounded-xl shadow border border-gray-100 px-5 py-3">
          <div>
            <h2 className="font-semibold text-gray-900 text-sm">Badge wall</h2>
            <p className="text-xs text-gray-500">Printable certification badges with QR verification codes</p>
          </div>
          <button
            onClick={() => setShowBadges(!showBadges)}
            data-testid="badges-toggle"
            className={`px-4 py-2 rounded-lg text-sm font-medium ${showBadges ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-violet-600 text-white hover:bg-violet-700'}`}
          >
            {showBadges ? 'Hide badges' : 'Show badges'}
          </button>
        </div>

        {/* S1 LARGE-tier: crews — the self-serve crew creation path. Without a
            Team row, project crew assignment and the /projects/crews picker are
            permanently empty for a fresh company. */}
        <div className="mb-6 bg-white rounded-xl shadow border border-gray-100 px-5 py-4">
          <h2 className="font-semibold text-gray-900 text-sm">Field crews</h2>
          <p className="text-xs text-gray-500 mb-3">Crews are used to assign people to projects.</p>
          {crews.length > 0 && (
            <ul className="mb-3 flex flex-wrap gap-2">
              {crews.map((c: any) => (
                <li key={c.id} data-testid="crew-chip" className="text-xs bg-violet-50 text-violet-800 border border-violet-200 rounded-full px-3 py-1">
                  {c.name} · {c._count?.users ?? 0} member{(c._count?.users ?? 0) === 1 ? '' : 's'}
                </li>
              ))}
            </ul>
          )}
          {crews.length === 0 && (
            <p className="text-xs text-gray-400 mb-3">No crews yet — create one below to enable project crew assignment.</p>
          )}
          <div className="flex gap-2 items-center flex-wrap" data-testid="crew-create">
            {/* S2 crew-naming: pick the foreman first — the name auto-defaults to
                "<Foreman>'s Crew"; the name field stays optional + editable. */}
            <select
              value={crewForemanId}
              onChange={(e) => setCrewForemanId(e.target.value ? Number(e.target.value) : '')}
              className="border rounded-lg px-3 py-2 text-sm max-w-xs"
              data-testid="crew-foreman-select"
            >
              <option value="">No foreman yet</option>
              {users
                .filter((u: any) => u.isActive && u.role === 'FOREMAN')
                .map((u: any) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </option>
                ))}
            </select>
            <input
              value={crewName}
              onChange={(e) => setCrewName(e.target.value)}
              placeholder={
                crewForemanId
                  ? `Defaults to “${users.find((u: any) => u.id === crewForemanId)?.firstName ?? ''} ${users.find((u: any) => u.id === crewForemanId)?.lastName ?? ''}'s Crew”`
                  : 'Crew name (optional with foreman)'
              }
              className="border rounded-lg px-3 py-2 text-sm flex-1 max-w-xs"
              data-testid="crew-name-input"
            />
            <button
              onClick={createCrew}
              disabled={crewSaving || (!crewName.trim() && !crewForemanId)}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700 disabled:opacity-50"
              data-testid="crew-create-button"
            >
              {crewSaving ? 'Creating…' : 'Create crew'}
            </button>
          </div>
        </div>

        {showBadges && (
          <div className="mb-6" data-testid="badges-grid">
            {loading ? (
              <div className="p-6 bg-white rounded-xl shadow border border-gray-100 text-sm text-gray-500">Loading…</div>
            ) : users.length === 0 ? (
              <div className="p-6 bg-white rounded-xl shadow border border-gray-100 text-sm text-gray-500">No one to show badges for yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 justify-items-center print:grid-cols-4 print:gap-2">
                {users.filter((u: any) => u.isActive && u.qrCode).map((u: any) => (
                  <div key={u.id}>
                    <button
                      type="button"
                      onClick={() => setExpandedBadgeUser(expandedBadgeUser?.id === u.id ? null : u)}
                      className="text-left"
                      title="Click to expand full badge"
                    >
                      <CertBadge
                        name={`${u.firstName} ${u.lastName}`}
                        role={u.role}
                        photoUrl={u.photoUrl}
                        qrCode={u.qrCode}
                        certs={u.certifications}
                        variant="compact"
                      />
                    </button>
                    {expandedBadgeUser?.id === u.id && (
                      <div className="mt-2 flex justify-center">
                        <CertBadge
                          name={`${u.firstName} ${u.lastName}`}
                          role={u.role}
                          photoUrl={u.photoUrl}
                          qrCode={u.qrCode}
                          certs={u.certifications}
                          variant="full"
                          print
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {showAdd && (
          <div className="mb-6 bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Add a new person</h2>
            {seatUsage && (
              <div data-testid="seat-usage" className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900">
                <p className="font-semibold">Seat capacity ({seatUsage.plan})</p>
                <p className="mt-1" data-testid="seat-usage-summary">{seatUsageSummary(seatUsage)}</p>
              </div>
            )}
            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First name *</label>
                <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last name *</label>
                <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-gray-400 font-normal">(optional — leave blank for a test/paper worker)</span>
                </label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Blank = auto-generated test email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                {form.password && (
                  <p className="text-xs text-gray-400 mt-1">Leaving email blank also generates a password — copy it when shown.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Temporary password <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Leave blank to send an invite"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                <p className="text-xs text-gray-400 mt-1">8+ characters, or blank to generate an invite token</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <button
              onClick={addUser}
              disabled={saving || !form.firstName.trim() || !form.lastName.trim()}
              className="mt-4 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add person'}
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl shadow border border-gray-100 divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : users.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No one else is in your company yet. Add your first person above.</div>
          ) : (
            users.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600">
                    {(u.firstName?.[0] ?? '') + (u.lastName?.[0] ?? '')}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {u.firstName} {u.lastName}
                      {u.isCompanyOwner && <span className="ml-2 text-xs font-normal text-amber-600">★ Owner</span>}
                      {!u.isActive && <span className="ml-2 text-xs font-normal text-gray-400">(inactive)</span>}
                    </p>
                    <p className="text-xs text-gray-500">{u.email}{u.phoneNumber ? ` · ${u.phoneNumber}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${ROLE_STYLES[u.role] ?? 'bg-gray-100 text-gray-600'}`}>
                    {ROLE_OPTIONS.find((r) => r.value === u.role)?.label ?? u.role}
                  </span>
                  {/* Fix 3 (UAT): admin user-management actions. Error payloads are shown verbatim. */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleResetPassword(u)}
                      className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >Reset password</button>
                    {u.isActive ? (
                      <button
                        onClick={() => handleDeactivate(u)}
                        className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                      >Deactivate</button>
                    ) : (
                      <button
                        onClick={() => handleReactivate(u)}
                        className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                      >Reactivate</button>
                    )}
                    <button
                      onClick={() => handleDeleteUser(u)}
                      className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 border border-red-100 hover:bg-red-100"
                    >Delete</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
